import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { apiClient } from 'lib/api';
import type { MutationConfig } from 'lib/react-query';
import { DEV_MODE } from '../constants';
import queryKeys from './queryKeys';
import { patchMockTwitterCreativeSettings } from './mockTwitterCreativeSettings';
import type { TwitterCreativeSettings, UpdateTwitterCreativeSettingsParams } from './types';

const MOCK_DELAY_MS = 300;

/** The backend answers an empty 200, so there is nothing to return. */
export const updateTwitterCreativeSettings = async ({
  settings_id,
  flows,
  ...patch
}: UpdateTwitterCreativeSettingsParams): Promise<void> => {
  // PROTOTYPE ONLY: mock branch, active while DEV_MODE is true.
  if (DEV_MODE) {
    await new Promise((resolve) => setTimeout(resolve, MOCK_DELAY_MS));
    patchMockTwitterCreativeSettings({ settings_id, flows, ...patch });
    return;
  }

  // The backend takes flow ids and replaces the whole set with them.
  const body = flows ? { ...patch, flows: flows.map((flow) => flow.id) } : patch;
  const res = await apiClient.patch(queryKeys.updateCreativeSettings(settings_id), body);

  if (isAxiosError(res)) {
    const error = new Error('Update X Creative Settings Failed');
    error.cause = {
      ...res.response?.data,
      status: res.response?.status,
    };
    return Promise.reject(error);
  }
};

interface UseUpdateTwitterCreativeSettingsOptions {
  config?: MutationConfig<typeof updateTwitterCreativeSettings>;
}

export const useUpdateTwitterCreativeSettings = ({ config }: UseUpdateTwitterCreativeSettingsOptions = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...config,
    mutationFn: updateTwitterCreativeSettings,
    onSuccess: async (...args) => {
      // The PATCH answers an empty body, so the change is applied to every cached list locally.
      const { settings_id, ...patch } = args[1];
      queryClient.setQueriesData<TwitterCreativeSettings[]>({ queryKey: [queryKeys.creativeSettings] }, (items) =>
        items?.map((item) => (item.settings_id === settings_id ? { ...item, ...patch } : item)),
      );

      if (config?.onSuccess) {
        await config.onSuccess(...args);
      }
    },
  });
};
